import React from 'react'
import { cn } from '@/lib/utils'

/**
 * CalendarPeek (W3) — the How-It-Works step-2 vignette: a mini month grid with
 * prices. On reveal a ghost cursor drifts in, a hover ring settles on one date,
 * and a compact three-line Rate Ledger hover-card pops beneath it — the same
 * "read the Ledger" moment the calendar gives you in the product, without a
 * single stage number or cadence word.
 *
 * Motion budget: this animates ONCE on reveal (cursor settle → card pop) then
 * rests in the popped state, so the section's only continuous animation stays
 * the ChangeLog ticker. prefers-reduced-motion / no IntersectionObserver → the
 * static popped state, card already open (a designed still).
 *
 * Copy is VERBATIM from docs/marketing/2026-07-05-landing3-copy.md §W3 — do not
 * edit strings here without updating that doc + the claims register. The hover
 * card carries NO stage numbering. Figures are illustrative marketing fiction
 * (a room-rate example, not a live price card) — the "no frontend math" product
 * rule does not apply; nothing is computed, the £ values are literal copy.
 */

// The single date the cursor settles on (its cell shows the ledger's final £120).
const TARGET_DAY = 12
const TARGET_PRICE = 120

// Verbatim from copy doc §W3 — three lines, in order. NO stage numbers.
const HOVER_LINES = ['Base £95', 'Season +10% → £105', 'Demand +6% → £120']

// A small illustrative month: weekend cells (Sat/Sun) priced a touch higher.
// Purely decorative texture behind the peek — figures are marketing fiction.
const WEEKDAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S']
interface Cell {
  day: number
  price: number
  weekend: boolean
}
function buildMonth(): Cell[] {
  // 1st falls on a Wednesday (2 leading blanks handled in render); 30 days.
  const cells: Cell[] = []
  for (let day = 1; day <= 30; day++) {
    // column 0..6 where the 1st sits at column 2 (Wed)
    const col = (day + 1) % 7
    const weekend = col === 5 || col === 6
    const price = weekend ? 112 : 95
    cells.push({ day, price, weekend })
  }
  return cells
}
const MONTH = buildMonth()
const LEADING_BLANKS = 2 // 1st is a Wednesday

const REVEAL_TO_SETTLE_MS = 650
const SETTLE_TO_POP_MS = 1050

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

export default function CalendarPeek({ className }: { className?: string }) {
  const ref = React.useRef<HTMLDivElement | null>(null)
  const [reduced] = React.useState(prefersReducedMotion)
  // phase: 0 idle · 1 cursor settled on target · 2 hover-card popped
  const [phase, setPhase] = React.useState(reduced ? 2 : 0)

  React.useEffect(() => {
    if (reduced) return
    const el = ref.current
    if (!el) return
    let timers: ReturnType<typeof setTimeout>[] = []
    const run = () => {
      timers.push(setTimeout(() => setPhase(1), REVEAL_TO_SETTLE_MS))
      timers.push(setTimeout(() => setPhase(2), SETTLE_TO_POP_MS))
    }
    if (typeof IntersectionObserver === 'undefined') {
      run()
      return () => timers.forEach(clearTimeout)
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          run()
          io.disconnect()
        }
      },
      { threshold: 0.4 }
    )
    io.observe(el)
    return () => {
      io.disconnect()
      timers.forEach(clearTimeout)
    }
  }, [reduced])

  const settled = phase >= 1
  const popped = phase >= 2

  return (
    <div
      ref={ref}
      className={cn('mkt3-panel relative overflow-hidden', className)}
      role="img"
      aria-label="A rate calendar with a hover card showing how one night's price was built, from a base rate to the final price."
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-3 border-b border-[var(--mkt3-hairline)] px-5 py-3.5">
        <span className="mkt3-label">Rate Calendar</span>
        <span className="font-mono text-[11px] tracking-[0.1em] text-[var(--mkt3-muted)]">July</span>
      </div>

      <div className="relative px-5 pb-6 pt-4">
        {/* Weekday header */}
        <div className="grid grid-cols-7 gap-1.5 pb-2">
          {WEEKDAYS.map((d, i) => (
            <span key={i} className="text-center font-mono text-[10px] font-medium uppercase tracking-[0.1em] text-slate-400">
              {d}
            </span>
          ))}
        </div>

        {/* Day grid */}
        <div className="grid grid-cols-7 gap-1.5">
          {Array.from({ length: LEADING_BLANKS }).map((_, i) => (
            <span key={`blank-${i}`} aria-hidden="true" />
          ))}
          {MONTH.map((cell) => {
            const isTarget = cell.day === TARGET_DAY
            return (
              <div
                key={cell.day}
                className={cn(
                  'relative flex aspect-square flex-col items-center justify-center rounded-md border text-center transition-colors duration-300',
                  isTarget && settled
                    ? 'border-teal-500 bg-teal-50'
                    : cell.weekend
                      ? 'border-transparent bg-slate-50'
                      : 'border-transparent bg-white'
                )}
              >
                <span className="font-mono text-[9px] leading-none text-slate-400">{cell.day}</span>
                <span
                  className={cn(
                    'mt-0.5 font-mono text-[10px] font-semibold leading-none tabular-nums sm:text-[11px]',
                    isTarget && settled ? 'text-teal-700' : 'text-[var(--mkt3-ink)]'
                  )}
                >
                  £{isTarget ? TARGET_PRICE : cell.price}
                </span>

                {/* Hover ring on the settled target cell */}
                {isTarget && settled && (
                  <span
                    aria-hidden="true"
                    className={cn(
                      'pointer-events-none absolute inset-[-3px] rounded-lg ring-2 ring-teal-500/60',
                      !reduced && 'mkt3-pop'
                    )}
                  />
                )}
              </div>
            )
          })}
        </div>

        {/* Ghost cursor — drifts to the target once, then rests on it */}
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute z-20 h-4 w-4 transition-all ease-out',
            reduced ? 'duration-0' : 'duration-700',
            settled ? 'opacity-90' : 'opacity-0'
          )}
          style={{
            // Nestled at the lower-right of the target cell. Approximate placement
            // over the grid; purely decorative so exact pixels don't matter.
            left: settled ? '30%' : '58%',
            top: settled ? '54%' : '30%',
          }}
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4 drop-shadow-sm">
            <path d="M4 2l6 15 2.2-6.2L18.5 8.5 4 2z" fill="#0f172a" stroke="#fff" strokeWidth="1.5" strokeLinejoin="round" />
          </svg>
        </span>

        {/* Compact 3-line ledger hover-card */}
        <div
          className={cn(
            'pointer-events-none absolute left-1/2 z-30 w-[15rem] -translate-x-1/2 rounded-xl border border-[var(--mkt3-hairline)] bg-white p-3.5 shadow-[0_18px_44px_-20px_rgba(15,23,42,0.4)] transition-all',
            reduced ? 'duration-0' : 'duration-300',
            popped ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
          )}
          style={{ bottom: '0.75rem' }}
        >
          <p className="mkt3-label mb-2">Sat · 1 night</p>
          <ul className="space-y-1.5">
            {HOVER_LINES.map((line, i) => {
              const last = i === HOVER_LINES.length - 1
              return (
                <li
                  key={i}
                  className={cn(
                    'flex items-center justify-between font-mono text-[12px] tabular-nums',
                    last ? 'font-semibold text-teal-700' : 'text-[var(--mkt3-body)]'
                  )}
                >
                  <span>{line}</span>
                </li>
              )
            })}
          </ul>
          <p className="mt-2.5 border-t border-[var(--mkt3-hairline)] pt-2 text-[11px] text-[var(--mkt3-muted)]">
            Every pound accounted for.
          </p>
        </div>
      </div>
    </div>
  )
}
